import { BASE_CATALOGUES, AMTRAK_BASE_RULES } from './agreementCatalogues';   // ruling 321 slice 3a (task list section 727): the catalogues are installation content, listed once
import { resolveAmtrakRule } from './ruleCatalog';   // section 736: the Amtrak resolver is code; it was re-exported through the content list
import { agreementFamily } from './agreementRegistry';   // ruling 312 phase 1 (d)

// Shared rule-text resolver — ONE source of truth (SS-XI) for turning a stored rule label
// ("Rule 18 — ...", "Article 20 — ...") into the governing agreement's catalog text.
// Used by the ClaimDetail review screen; the RuleBasisPicker resolves the same catalogs at
// filing time. Returns null for un-catalogued agreements so callers fall
// back gracefully. One standard: displayed text comes from the catalog, never AI.
//
// G8.6 (2026-09-14, task list section 222). `asOfDate` was accepted and then IGNORED for three of
// the four catalogues - the Amtrak path applies a dated modification chain (AMTRAK_RULE_MODS); the
// Conrail, CSX and D&H catalogues carry base text only, with NO modification records (each file's
// own header says so). A parameter accepted and discarded is worse than one absent, so the three
// now do the two things that CAN be done truthfully with a date:
//   1. Refuse a date before the agreement was in force - status 'not_yet_effective' - so a rule is
//      never shown as governing an incident that predates the agreement.
//   2. Say plainly, on the returned object, that no modification chain exists for the catalogue, so
//      the text is the base agreement as printed and later national/IMOU changes are not layered in.
// What this does NOT do: invent a modification history. When a dated instrument for one of these
// three is catalogued from its source, it goes into a MODS table beside the rules, and this resolver
// applies it the way the Amtrak path does. Effective dates are the catalogue headers' own.
// BASE_CATALOGUES now lives in src\data\agreementCatalogues.js (ruling 321 slice 3a) - the one list of the catalogues this
// installation carries; this resolver is craft-neutral and reads it.

function resolveBaseCatalogue(cat, num, asOfDate) {
  const r = cat.rules[num] || {};
  const asOf = (typeof asOfDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(asOfDate)) ? asOfDate.slice(0, 10) : null;
  const out = {
    title: r.title,
    text: r.text || '',
    status: r.title ? 'active' : 'unknown',
    asOf: asOf,
    effective: cat.effective,
    modificationsCatalogued: false,
    qualifier: 'Base text of the ' + cat.label + ' as printed; no dated modification is catalogued for this agreement, so later changes are not reflected here.',
  };
  // Section 522: a catalogue whose rules carry their own modification record (Conrail M/S) says so per rule.
  if (r.modified) { out.modificationsCatalogued = true; out.modified = r.modified; out.qualifier = r.modified + ' The text shown is the rule as modified.'; }
  else if (cat.machineExtracted) out.qualifier = 'Text of the ' + cat.label + ' as printed; no later modification of this rule is catalogued.';
  if (cat.machineExtracted) out.qualifier += ' Machine-extracted from the agreement scan and not yet transcription-verified - read the quotation off the agreement page before it is filed.';
  if (asOf && asOf < cat.effective) {
    out.status = 'not_yet_effective';
    out.qualifier = 'The ' + cat.label + ' took effect ' + cat.effective + '; the incident date ' + asOf + ' is before it. This text did not govern on that date.';
  }
  return out;
}

// The rules object of a catalogue family, or null when this installation carries no catalogue for it. Moved here from the
// content list (section 736) so the content is data only; the picker reads it from here.
export function catalogueRules(family) {
  if (family === 'amtrak-ym') return AMTRAK_BASE_RULES;
  const hit = BASE_CATALOGUES.find((c) => c.family === family);
  return hit ? hit.rules : null;
}

export function resolveRuleText(agreement, ruleRef, location, asOfDate) {
  const a = agreement || '';
  const num = (String(ruleRef).match(/\d+/) || [])[0];
  if (!num) return null;
  const family = agreementFamily(a);   // ruling 312 phase 1 (d): the registry names the agreement
  if (family === 'amtrak-ym') return resolveAmtrakRule(num, location, asOfDate);
  for (const cat of BASE_CATALOGUES) {
    if (cat.family === family) return resolveBaseCatalogue(cat, num, asOfDate);
  }
  return null;
}
